/**
 * BestSlotChips — computes the next occurrence in IST of each of the top
 * 3-5 (day-of-week, hour) slots from meta_best_time_v and renders them
 * as clickable chips. Clicking one fills the datetime-local input.
 */
import { useMemo } from 'react';
import { TrendingUp } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useBestTime, type BestTimeCell } from '@/hooks/useBestTime';

const DOW_LABEL = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

interface SuggestedSlot {
  iso: string;             // datetime-local value (local timezone)
  label: string;           // "Tue 18:00"
  when: Date;              // for sorting
  engagement: number;      // avg engagement %
  isBest: boolean;         // top-1 flag
}

/** Next occurrence of (dow, hour) — in the browser's local zone since the
 *  datetime-local input uses local time. IST vs local drift is usually zero
 *  since the team is in India, so this stays intuitive. */
function nextSlotAt(dow: number, hour: number, now = new Date()): Date {
  const d = new Date(now);
  d.setHours(hour, 0, 0, 0);
  let diff = dow - d.getDay();
  if (diff < 0 || (diff === 0 && d <= now)) diff += 7;
  d.setDate(d.getDate() + diff);
  return d;
}

function toLocalDateTimeString(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function BestSlotChips({ selected, onPick }: { selected: string | null; onPick: (iso: string) => void }) {
  const bt = useBestTime();

  const suggestions: SuggestedSlot[] = useMemo(() => {
    const cells = (bt.data ?? []).filter((c) => c.avg_engagement_pct != null).slice(0, 5);
    if (cells.length === 0) return [];
    const now = new Date();
    return cells.map((c: BestTimeCell, i) => {
      const when = nextSlotAt(c.dow, c.hour_ist, now);
      return {
        iso: toLocalDateTimeString(when),
        label: `${DOW_LABEL[c.dow]} ${String(c.hour_ist).padStart(2, '0')}:00`,
        when,
        engagement: c.avg_engagement_pct ?? 0,
        isBest: i === 0,
      };
    }).sort((a, b) => a.when.getTime() - b.when.getTime());
  }, [bt.data]);

  if (bt.isLoading || suggestions.length === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="flex items-center gap-1 text-2xs text-muted-foreground">
        <TrendingUp className="h-3 w-3" /> Best slots:
      </span>
      {suggestions.map((s) => {
        const isSel = selected?.startsWith(s.iso.slice(0, 16));
        return (
          <button
            key={s.iso}
            type="button"
            onClick={() => onPick(s.iso)}
            className={cn(
              'rounded-md border px-2 py-1 text-2xs font-medium tabular-nums transition-colors',
              isSel
                ? 'border-violet-500 bg-violet-500 text-white'
                : s.isBest
                  ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 hover:border-emerald-500'
                  : 'border-border/50 text-muted-foreground hover:border-border hover:text-foreground',
            )}
            title={`${s.label} · ${s.engagement.toFixed(2)}% avg engagement · fires ${s.when.toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}`}
          >
            {s.label} · {s.engagement.toFixed(1)}%
          </button>
        );
      })}
    </div>
  );
}
